/**
 * PendingClassifications Component
 *
 * Displays a table of documents awaiting manual classification.
 * Allows resolving (assigning to a client) or dismissing items.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Check,
  X,
  AlertCircle,
  FileCheck,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { PendingClassification, Client } from '@/types/management';

type SortColumn = 'documentTitle' | 'source' | 'sourceDate' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

function compareValues(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: SortDirection,
  isDate: boolean,
): number {
  const aVal = a ?? null;
  const bVal = b ?? null;
  if (aVal === null && bVal === null) return 0;
  if (aVal === null) return 1;
  if (bVal === null) return -1;

  let result: number;
  if (isDate) {
    result = new Date(aVal).getTime() - new Date(bVal).getTime();
  } else {
    result = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
  }

  return direction === 'asc' ? result : -result;
}

function SortIcon({
  column,
  sortState,
}: {
  column: SortColumn;
  sortState: SortState;
}) {
  if (sortState.column !== column) {
    return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground/50" />;
  }
  return sortState.direction === 'asc' ? (
    <ArrowUp className="ml-1 h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 h-3 w-3" />
  );
}

interface PendingClassificationsProps {
  items: PendingClassification[];
  clients: Client[];
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
  onResolve: (id: string, clientId: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
}

function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function formatTier(tier: string): string {
  const tierNames: Record<string, string> = {
    contact_match: 'Contact',
    name_match: 'Name',
    keyword_match: 'Keyword',
    semantic_match: 'Semantic',
    none: 'None',
  };
  return tierNames[tier] || tier;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function PendingClassifications({
  items,
  clients,
  isLoading,
  hasFetched,
  error,
  onResolve,
  onDismiss,
}: PendingClassificationsProps) {
  const [sortState, setSortState] = useState<SortState>({
    column: 'sourceDate',
    direction: 'desc',
  });
  const [selectedClientIds, setSelectedClientIds] = useState<
    Record<string, string>
  >({});
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [dismissTarget, setDismissTarget] =
    useState<PendingClassification | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSort = useCallback((column: SortColumn) => {
    setSortState((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const sortedItems = useMemo(() => {
    const { column, direction } = sortState;
    return [...items].sort((a, b) => {
      const isDate = column === 'sourceDate' || column === 'createdAt';
      return compareValues(a[column], b[column], direction, isDate);
    });
  }, [items, sortState]);

  const handleClientSelect = useCallback(
    (pendingId: string, clientId: string) => {
      setSelectedClientIds((prev) => ({ ...prev, [pendingId]: clientId }));
      setActionError(null);
    },
    [],
  );

  const handleResolve = useCallback(
    async (pending: PendingClassification) => {
      const clientId = selectedClientIds[pending.id];
      if (!clientId) {
        setActionError('Please select a client');
        return;
      }

      setProcessingIds((prev) => new Set(prev).add(pending.id));
      setActionError(null);

      try {
        await onResolve(pending.id, clientId);
      } catch (err) {
        setActionError(
          err instanceof Error ? err.message : 'Failed to resolve',
        );
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(pending.id);
          return next;
        });
      }
    },
    [selectedClientIds, onResolve],
  );

  const handleDismiss = useCallback(async () => {
    if (!dismissTarget) return;

    setProcessingIds((prev) => new Set(prev).add(dismissTarget.id));
    setActionError(null);

    try {
      await onDismiss(dismissTarget.id);
      setDismissTarget(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to dismiss');
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(dismissTarget.id);
        return next;
      });
    }
  }, [dismissTarget, onDismiss]);

  // Show skeleton while loading OR before first successful fetch completes
  if (isLoading || (items.length === 0 && !hasFetched)) {
    return (
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Source Date</TableHead>
              <TableHead>Classified</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Assign To</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-28" />
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-10" />
                    <Skeleton className="h-3 w-14" />
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-9 w-[180px]" />
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-8" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive p-4 border rounded-md">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  }

  // Empty state only after we've confirmed there are no items (hasFetched is true)
  if (items.length === 0) {
    return (
      <EmptyState
        icon={FileCheck}
        title="No pending classifications"
        description="Documents will appear here when they need manual review."
      />
    );
  }

  return (
    <>
      {actionError && (
        <div className="flex items-center gap-2 text-destructive p-3 mb-4 border border-destructive/20 rounded-md bg-destructive/10">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{actionError}</span>
        </div>
      )}

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('documentTitle')}
              >
                <span className="inline-flex items-center">
                  Document
                  <SortIcon column="documentTitle" sortState={sortState} />
                </span>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('source')}
              >
                <span className="inline-flex items-center">
                  Source
                  <SortIcon column="source" sortState={sortState} />
                </span>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('sourceDate')}
              >
                <span className="inline-flex items-center">
                  Source Date
                  <SortIcon column="sourceDate" sortState={sortState} />
                </span>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => handleSort('createdAt')}
              >
                <span className="inline-flex items-center">
                  Classified
                  <SortIcon column="createdAt" sortState={sortState} />
                </span>
              </TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Assign To</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedItems.map((item) => {
              const isProcessing = processingIds.has(item.id);
              const selectedClient = selectedClientIds[item.id] || '';

              return (
                <TableRow key={item.id}>
                  <TableCell className="max-w-[420px]">
                    {item.documentUrl ? (
                      <a
                        href={item.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline block truncate"
                      >
                        {item.documentTitle}
                      </a>
                    ) : (
                      <div className="font-medium truncate">
                        {item.documentTitle}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.sourceUrl ? (
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Badge
                          variant="outline"
                          className="cursor-pointer hover:bg-accent gap-1"
                        >
                          {item.source}
                          <ExternalLink className="h-3 w-3" />
                        </Badge>
                      </a>
                    ) : (
                      <Badge variant="outline">{item.source}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {item.sourceDate ? formatDate(item.sourceDate) : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(item.createdAt)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="space-y-1 cursor-help">
                          <div className="font-medium">
                            {formatConfidence(item.confidence)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatTier(item.tier)}
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {item.suggestedClientName &&
                        item.suggestedClientName !== 'default'
                          ? `Suggested: ${item.suggestedClientName}`
                          : 'No confident match'}
                      </TooltipContent>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={selectedClient}
                      onValueChange={(value) =>
                        handleClientSelect(item.id, value)
                      }
                      disabled={isProcessing}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResolve(item)}
                            disabled={isProcessing || !selectedClient}
                            aria-label="Approve classification"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Assign to selected client
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDismissTarget(item)}
                            disabled={isProcessing}
                            aria-label="Dismiss classification"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Dismiss without assigning
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="text-sm text-muted-foreground mt-2">
        {items.length} item{items.length !== 1 ? 's' : ''} pending review
      </div>

      {/* Dismiss Confirmation Dialog */}
      <AlertDialog
        open={!!dismissTarget}
        onOpenChange={() => setDismissTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss Classification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to dismiss this classification? The document
              &quot;{dismissTarget?.documentTitle}&quot; will remain
              unclassified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={processingIds.has(dismissTarget?.id || '')}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDismiss}
              disabled={processingIds.has(dismissTarget?.id || '')}
            >
              {processingIds.has(dismissTarget?.id || '')
                ? 'Dismissing...'
                : 'Dismiss'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
